import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Upload, X, Calendar, Image, Users, Building2, Search } from 'lucide-react';
import { projectService, type CreateProjectData } from '@/lib/projectService';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { uploadToR2 } from '@/lib/r2-storage';
import { ImageUploadModal } from '@/components/brand/image-upload/ImageUploadModal';

interface CreateProjectDialogProps {
  onProjectCreated?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}

export const CreateProjectDialog: React.FC<CreateProjectDialogProps> = ({
  onProjectCreated,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}) => {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange !== undefined ? onOpenChange : setInternalOpen;
  const [step, setStep] = useState(1);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rate, setRate] = useState('');
  const [rateType, setRateType] = useState<'fixed' | 'hourly'>('fixed');
  const [coverImage, setCoverImage] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [brandId, setBrandId] = useState('');
  const [creating, setCreating] = useState(false);
  const [coverUploadOpen, setCoverUploadOpen] = useState(false);
  const [stages, setStages] = useState<{ name: string; start_date?: string; end_date?: string }[]>([]);
  const [newStage, setNewStage] = useState('');
  const [newStageStart, setNewStageStart] = useState('');
  const [newStageEnd, setNewStageEnd] = useState('');
  const [editingStageIndex, setEditingStageIndex] = useState<number | null>(null);

  // Available brands
  const [brands, setBrands] = useState<{ id: string; name: string; logo_url: string | null }[]>([]);
  // Available clients in workspace
  const [workspaceClients, setWorkspaceClients] = useState<any[]>([]);
  const [selectedClients, setSelectedClients] = useState<{ userId: string; permission: 'edit' | 'view' }[]>([]);
  // Available companies in workspace
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  // Available team members
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<string[]>([]);
  // Search states
  const [clientSearch, setClientSearch] = useState('');
  const [memberSearch, setMemberSearch] = useState('');

  useEffect(() => {
    if (open && currentWorkspace) {
      loadBrands();
      loadClients();
      loadCompanies();
      loadTeamMembers();
    }
  }, [open, currentWorkspace?.id]);

  const loadBrands = async () => {
    if (!currentWorkspace) return;
    const { data } = await supabase
      .from('brands')
      .select('id, name, logo_url')
      .eq('workspace_id', currentWorkspace.id);
    setBrands(data || []);
  };

  const loadClients = async () => {
    if (!currentWorkspace) return;
    try {
      const clients = await projectService.getWorkspaceClients(currentWorkspace.id);
      setWorkspaceClients(clients);
    } catch {
      setWorkspaceClients([]);
    }
  };

  const loadCompanies = async () => {
    if (!currentWorkspace) return;
    try {
      const data = await projectService.getWorkspaceCompanies(currentWorkspace.id);
      setCompanies(data);
    } catch {
      setCompanies([]);
    }
  };

  const loadTeamMembers = async () => {
    if (!currentWorkspace) return;
    const { data } = await supabase
      .from('workspace_members')
      .select('user_id, role')
      .eq('workspace_id', currentWorkspace.id)
      .eq('status', 'active')
      .neq('role', 'client');

    if (!data) return;
    const userIds = data.map(m => m.user_id).filter(id => id !== user?.id);
    if (userIds.length === 0) { setTeamMembers([]); return; }

    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, first_name, last_name, email, avatar_url')
      .in('id', userIds);

    setTeamMembers((profiles || []).map(p => ({
      id: p.id,
      name: `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.email?.split('@')[0],
      email: p.email,
      avatar: p.avatar_url,
    })));
  };

  const handleCoverImageSelect = async (imageData: string, metadata?: any) => {
    let finalUrl = imageData;
    if (metadata?.source === 'upload' && metadata.file && currentWorkspace) {
      try {
        const res = await uploadToR2({
          fileName: metadata.file.name,
          file: metadata.file,
          folder: `workspace_media/${currentWorkspace.id}/projects`,
        });
        finalUrl = res.publicUrl;
      } catch (err) {
        console.error('Error uploading cover image:', err);
        toast.error('Failed to upload cover image');
        return;
      }
    }
    setCoverImage(finalUrl);
  };

  const toggleClient = (clientId: string) => {
    setSelectedClients(prev => {
      const exists = prev.find(c => c.userId === clientId);
      if (exists) return prev.filter(c => c.userId !== clientId);
      return [...prev, { userId: clientId, permission: 'view' as const }];
    });
  };

  const updateClientPermission = (clientId: string, permission: 'edit' | 'view') => {
    setSelectedClients(prev =>
      prev.map(c => c.userId === clientId ? { ...c, permission } : c)
    );
  };

  const toggleCompany = (companyId: string) => {
    setSelectedCompanies(prev =>
      prev.includes(companyId) ? prev.filter(id => id !== companyId) : [...prev, companyId]
    );
  };

  const toggleTeamMember = (memberId: string) => {
    setSelectedTeamMembers(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const addStage = () => {
    if (!newStage.trim()) return;
    if (editingStageIndex !== null) {
      // Save edit
      setStages(prev => prev.map((s, i) => i === editingStageIndex ? {
        name: newStage.trim(),
        start_date: newStageStart || undefined,
        end_date: newStageEnd || undefined
      } : s));
      setEditingStageIndex(null);
    } else {
      setStages(prev => [...prev, {
        name: newStage.trim(),
        start_date: newStageStart || undefined,
        end_date: newStageEnd || undefined
      }]);
    }
    setNewStage('');
    setNewStageStart('');
    setNewStageEnd('');
  };

  const editStage = (index: number) => {
    const s = stages[index];
    setNewStage(s.name);
    setNewStageStart(s.start_date || '');
    setNewStageEnd(s.end_date || '');
    setEditingStageIndex(index);
  };

  const cancelEdit = () => {
    setNewStage('');
    setNewStageStart('');
    setNewStageEnd('');
    setEditingStageIndex(null);
  };

  const removeStage = (index: number) => {
    setStages(prev => prev.filter((_, i) => i !== index));
    if (editingStageIndex === index) cancelEdit();
  };

  const handleCreate = async () => {
    if (!currentWorkspace || !user) {
      toast.error('Workspace not loaded');
      return;
    }
    if (!name.trim()) {
      toast.error('Project name is required');
      return;
    }

    try {
      setCreating(true);
      const data: CreateProjectData = {
        workspaceId: currentWorkspace.id,
        userId: user.id,
        name: name.trim(),
        description: description.trim(),
        coverImage: coverImage || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        brandId: brandId || undefined,
        clientIds: selectedClients.length > 0 ? selectedClients : undefined,
        companyIds: selectedCompanies.length > 0 ? selectedCompanies : undefined,
        teamMemberIds: selectedTeamMembers.length > 0 ? selectedTeamMembers : undefined,
        rate: rate ? parseFloat(rate) : undefined,
        rateType: rateType,
        stages: stages.map(s => ({
          id: Math.random().toString(36).substring(2, 11),
          name: s.name,
          is_completed: false,
          start_date: s.start_date,
          end_date: s.end_date
        })),
      };

      await projectService.createProject(data);
      setOpen(false);
      resetForm();
      onProjectCreated?.();
      toast.success('Project created successfully');
    } catch (error: any) {
      console.error('Error creating project:', error);
      toast.error(error.message || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setRate('');
    setRateType('fixed');
    setCoverImage('');
    setStartDate('');
    setEndDate('');
    setBrandId('');
    setSelectedClients([]);
    setSelectedCompanies([]);
    setSelectedTeamMembers([]);
    setStages([]);
    setNewStage('');
    setNewStageStart('');
    setNewStageEnd('');
    setEditingStageIndex(null);
    setClientSearch('');
    setMemberSearch('');
    setStep(1);
  };
  const filteredClients = workspaceClients.filter(c => !clientSearch || (c.name || '').toLowerCase().includes(clientSearch.toLowerCase()) || (c.email || '').toLowerCase().includes(clientSearch.toLowerCase()));
  const filteredCompanies = companies.filter(c => !clientSearch || (c.name || '').toLowerCase().includes(clientSearch.toLowerCase()));
  const filteredMembers = teamMembers.filter(m => !memberSearch || (m.name || '').toLowerCase().includes(memberSearch.toLowerCase()) || (m.email || '').toLowerCase().includes(memberSearch.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button className="bg-workspace-accent text-white hover:bg-workspace-accent-hover">
            <Plus className="mr-2 h-4 w-4" /> New Project
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-card border-border">
        <DialogDescription className="sr-only">Create a new project</DialogDescription>
        <DialogHeader>
          <DialogTitle className="text-foreground text-xl">Create New Project</DialogTitle>


        </DialogHeader>

        {/* ── STEP 1: Project Details ── */}
        {step === 1 && (
          <div className="grid md:grid-cols-2 gap-8 py-2">
            {/* Left Column */}
            <div className="space-y-5">
              {/* Cover Image */}
              <div className="space-y-2">
                <Label className="text-foreground">Cover Image</Label>
                <div
                  className="h-36 rounded-lg border-2 border-dashed border-border bg-muted/50 flex items-center justify-center cursor-pointer hover:bg-muted/80 transition-colors relative overflow-hidden"
                  onClick={() => setCoverUploadOpen(true)}
                >
                  {coverImage ? (
                    <>
                      <img src={coverImage} alt="Cover" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Upload className="w-6 h-6 text-white" />
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); setCoverImage(''); }}
                        className="absolute top-2 right-2 p-1 bg-destructive text-white rounded-full hover:bg-destructive/90"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </>
                  ) : (
                    <div className="text-center">
                      <Image className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                      <span className="text-sm text-muted-foreground">Click to upload cover image</span>
                    </div>
                  )}
                </div>
                <ImageUploadModal
                  isOpen={coverUploadOpen}
                  onClose={() => setCoverUploadOpen(false)}
                  onImageSelect={(img, meta) => { handleCoverImageSelect(img, meta); setCoverUploadOpen(false); }}
                  isGlobalDarkMode={false}
                  title="Upload Cover Image"
                />
              </div>

              {/* Project Name */}
              <div className="space-y-2">
                <Label className="text-foreground" htmlFor="project-name">Project Name *</Label>
                <Input
                  id="project-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter project name"
                  className="bg-background border-input text-foreground"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label className="text-foreground" htmlFor="project-desc">Description</Label>
                <Textarea
                  id="project-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of the project"
                  className="bg-background border-input text-foreground resize-none"
                  rows={3}
                />
              </div>

              {/* Billing Type & Rate */}
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-foreground">Billing Type</Label>
                  <div className="grid grid-cols-2 gap-2 bg-muted/30 p-1 rounded-lg border border-border">
                    <button
                      type="button"
                      onClick={() => setRateType('fixed')}
                      className={`py-1.5 text-xs font-semibold rounded-md transition-all duration-200 ${
                        rateType === 'fixed'
                          ? 'bg-workspace-accent text-white shadow-sm'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      Fixed Price
                    </button>
                    <button
                      type="button"
                      onClick={() => setRateType('hourly')}
                      className={`py-1.5 text-xs font-semibold rounded-md transition-all duration-200 ${
                        rateType === 'hourly'
                          ? 'bg-workspace-accent text-white shadow-sm'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      Hourly Rate
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-foreground" htmlFor="project-rate">
                    {rateType === 'fixed' ? 'Project Budget ($)' : 'Hourly Rate ($/hr)'}
                  </Label>
                  <Input
                    id="project-rate"
                    type="number"
                    min="0"
                    step="0.01"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                    placeholder={rateType === 'fixed' ? 'Enter project budget (e.g. 5000)' : 'Enter hourly rate (e.g. 85)'}
                    className="bg-background border-input text-foreground"
                  />
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-5">
              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-foreground">Start Date</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="bg-background border-input text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">End Date</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="bg-background border-input text-foreground"
                  />
                </div>
              </div>

              {/* Link Clients & Companies */}
              {(workspaceClients.length > 0 || companies.length > 0) && (
                <div className="space-y-2">
                  <Label className="text-foreground flex items-center gap-2">
                    <Users className="w-4 h-4" /> Link Clients & Companies
                  </Label>

                  <Tabs defaultValue="clients" className="w-full border border-border rounded-lg overflow-hidden">
                    <TabsList className="w-full flex bg-muted/20 border-b border-border rounded-none p-0 h-10">
                      <TabsTrigger value="clients" className="flex-1 rounded-none data-[state=active]:bg-background data-[state=active]:border-b-2 data-[state=active]:border-workspace-accent">
                        Individual Clients
                      </TabsTrigger>
                      <TabsTrigger value="companies" className="flex-1 rounded-none data-[state=active]:bg-background data-[state=active]:border-b-2 data-[state=active]:border-workspace-accent">
                        Companies
                      </TabsTrigger>
                    </TabsList>
                    
                    <div className="p-2 border-b border-border/50 bg-background/50">
                      <div className="relative">
                        <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                        <Input 
                          placeholder="Search clients or companies..." 
                          value={clientSearch}
                          onChange={(e) => setClientSearch(e.target.value)}
                          className="h-9 pl-9 bg-background border-border focus-visible:ring-1" 
                        />
                      </div>
                    </div>

                    <TabsContent value="clients" className="m-0">
                      <div className="space-y-2 max-h-48 overflow-y-auto p-3 bg-background">
                        {filteredClients.length > 0 ? filteredClients.map(client => {
                          const selected = selectedClients.find(c => c.userId === client.id);
                          return (
                            <div key={client.id} className="flex items-center justify-between hover:bg-muted/30 p-1.5 rounded-lg transition-colors">
                              <label className="flex items-center gap-3 cursor-pointer w-full">
                                <input
                                  type="checkbox"
                                  checked={!!selected}
                                  onChange={() => toggleClient(client.id)}
                                  className="rounded border-border accent-workspace-accent"
                                />
                                <div className="flex items-center gap-2">
                                  <div className="w-7 h-7 rounded-full bg-workspace-accent/20 flex items-center justify-center text-xs font-semibold text-workspace-accent overflow-hidden shrink-0">
                                    {client.avatar ? (
                                      <img src={client.avatar} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      (client.name?.[0] || 'C').toUpperCase()
                                    )}
                                  </div>
                                  <div>
                                    <div className="text-sm font-medium text-foreground">{client.name}</div>
                                    <div className="text-xs text-muted-foreground">{client.email}</div>
                                  </div>
                                </div>
                              </label>
                              {selected && (
                                <Select value={selected.permission} onValueChange={(v: 'edit' | 'view') => updateClientPermission(client.id, v)}>
                                  <SelectTrigger className="w-24 h-8 text-xs bg-background border-input">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-popover border-border">
                                    <SelectItem value="view">View</SelectItem>
                                    <SelectItem value="edit">Edit</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          );
                        }) : (
                          <p className="text-sm text-muted-foreground text-center py-4">No clients available</p>
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="companies" className="m-0">
                      <div className="space-y-2 max-h-48 overflow-y-auto p-3 bg-background">
                        {filteredCompanies.length > 0 ? filteredCompanies.map(company => (
                          <label key={company.id} className="flex items-center gap-3 cursor-pointer hover:bg-muted/30 p-1.5 rounded-lg transition-colors">
                            <input
                              type="checkbox"
                              checked={selectedCompanies.includes(company.id)}
                              onChange={() => toggleCompany(company.id)}
                              className="rounded border-border accent-workspace-accent"
                            />
                            <div className="flex items-center gap-2 w-full">
                              <div className="w-7 h-7 rounded-lg bg-workspace-accent/10 flex items-center justify-center text-workspace-accent shrink-0">
                                {company.logo_url ? (
                                  <img src={company.logo_url} alt="" className="w-full h-full object-cover rounded-lg" />
                                ) : (
                                  <Building2 className="w-4 h-4" />
                                )}
                              </div>
                              <div className="flex-1 flex items-center justify-between">
                                <div className="text-sm font-medium text-foreground">{company.name}</div>
                                <div className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground">
                                  {company.clientCount} clients
                                </div>
                              </div>
                            </div>
                          </label>
                        )) : (
                          <p className="text-sm text-muted-foreground text-center py-4">No companies available</p>
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>
                  <p className="text-[11px] text-muted-foreground italic pl-1">
                    Selecting a company will automatically add all its linked clients as viewers.
                  </p>
                </div>
              )}

              {/* Assign Members */}
              {teamMembers.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-foreground flex items-center gap-2">
                    <Users className="w-4 h-4" /> Assign Members
                  </Label>
                  <div className="border border-border rounded-lg bg-muted/30 overflow-hidden">
                    <div className="p-2 border-b border-border/50 bg-background/50">
                      <div className="relative">
                        <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                        <Input 
                          placeholder="Search team members..." 
                          value={memberSearch}
                          onChange={(e) => setMemberSearch(e.target.value)}
                          className="h-9 pl-9 bg-background border-border focus-visible:ring-1" 
                        />
                      </div>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto p-3">
                      {filteredMembers.map(member => (
                        <label key={member.id} className="flex items-center gap-3 cursor-pointer hover:bg-muted/30 p-1.5 rounded-lg transition-colors">
                          <input
                            type="checkbox"
                            checked={selectedTeamMembers.includes(member.id)}
                            onChange={() => toggleTeamMember(member.id)}
                            className="rounded border-border"
                          />
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-violet-500/20 flex items-center justify-center text-xs font-semibold text-violet-600 dark:text-violet-400">
                              {member.avatar ? (
                                <img src={member.avatar} alt="" className="w-full h-full rounded-full object-cover" />
                              ) : (
                                (member.name?.[0] || 'T').toUpperCase()
                              )}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-foreground">{member.name}</div>
                              <div className="text-xs text-muted-foreground">{member.email}</div>
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Brand Selection */}
              {brands.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-foreground">Brand (Optional)</Label>
                  <Select value={brandId} onValueChange={setBrandId}>
                    <SelectTrigger className="bg-background border-input text-foreground">
                      <SelectValue placeholder="Select a brand" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      <SelectItem value="none">No brand</SelectItem>
                      {brands.map(b => (
                        <SelectItem key={b.id} value={b.id}>
                          <div className="flex items-center gap-2">
                            {b.logo_url && <img src={b.logo_url} alt="" className="w-5 h-5 rounded object-cover" />}
                            {b.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── STEP 2: Milestones ── */}
        {step === 2 && (
          <div className="py-2 space-y-4">
            <div className="space-y-1">
              <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                <Plus className="w-4 h-4" /> Project Milestones
              </h3>
              <p className="text-xs text-muted-foreground">
                Define key stages or checkpoints for your project. Progress will be tracked based on these. This step is optional.
              </p>
            </div>

            <div className="space-y-3 p-4 rounded-xl bg-muted/20 border border-border">
              {editingStageIndex !== null && (
                <div className="flex items-center gap-2 text-xs font-semibold text-workspace-accent">
                  <span>Editing milestone #{editingStageIndex + 1}</span>
                  <button onClick={cancelEdit} className="ml-auto text-muted-foreground hover:text-foreground transition-colors text-[10px] uppercase font-black">Cancel Edit</button>
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase font-black text-muted-foreground">Stage Name</Label>
                <Input
                  value={newStage}
                  onChange={(e) => setNewStage(e.target.value)}
                  placeholder="e.g. Discovery Call"
                  className="bg-background border-input text-sm h-9"
                  onKeyDown={(e) => { if (e.key === 'Enter') addStage(); }}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase font-black text-muted-foreground">Start Date</Label>
                  <Input
                    type="date"
                    value={newStageStart}
                    onChange={(e) => setNewStageStart(e.target.value)}
                    className="bg-background border-input text-xs h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase font-black text-muted-foreground">End Date</Label>
                  <Input
                    type="date"
                    value={newStageEnd}
                    onChange={(e) => setNewStageEnd(e.target.value)}
                    className="bg-background border-input text-xs h-9"
                  />
                </div>
              </div>
              <Button
                type="button"
                variant={editingStageIndex !== null ? 'default' : 'outline'}
                onClick={addStage}
                disabled={!newStage.trim()}
                className={`w-full h-9 text-xs font-bold ${editingStageIndex !== null ? 'bg-workspace-accent text-white hover:bg-workspace-accent-hover' : ''}`}
              >
                <Plus className="w-3 h-3 mr-2" /> {editingStageIndex !== null ? 'Save Changes' : 'Add Milestone'}
              </Button>
            </div>

            {stages.length > 0 && (
              <div className="space-y-2">
                {stages.map((stage, index) => (
                  <div
                    key={index}
                    onClick={() => editStage(index)}
                    className={`p-3 rounded-xl border group relative cursor-pointer transition-all ${
                      editingStageIndex === index
                        ? 'bg-workspace-accent/5 border-workspace-accent ring-1 ring-workspace-accent/30'
                        : 'bg-background border-border hover:border-workspace-accent/50 hover:bg-muted/30'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-bold text-foreground flex items-center gap-1.5">
                        {stage.name}
                        <span className="opacity-0 group-hover:opacity-60 transition-opacity">
                          <svg className="w-3 h-3 inline" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </span>
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); removeStage(index); }}
                        className="text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {(stage.start_date || stage.end_date) && (
                      <div className="flex items-center gap-3 text-[10px] text-muted-foreground font-medium">
                        {stage.start_date && (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> {new Date(stage.start_date).toLocaleDateString()}
                          </div>
                        )}
                        {stage.start_date && stage.end_date && <span>→</span>}
                        {stage.end_date && (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> {new Date(stage.end_date).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {stages.length === 0 && (
              <p className="text-[11px] text-muted-foreground italic text-center py-2">
                No milestones added yet — you can skip this step.
              </p>
            )}
          </div>
        )}

        <DialogFooter className="flex items-center justify-between gap-2 pt-2">
          <div className="flex-1">
            {step === 2 && (
              <Button variant="ghost" onClick={() => setStep(1)} className="text-muted-foreground">
                ← Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            {step === 1 ? (
              <Button
                className="bg-workspace-accent text-white hover:bg-workspace-accent-hover"
                onClick={() => setStep(2)}
                disabled={!name.trim()}
              >
                Next
              </Button>
            ) : (
              <Button
                className="bg-workspace-accent text-white hover:bg-workspace-accent-hover"
                onClick={handleCreate}
                disabled={creating || !name.trim()}
              >
                {creating ? 'Creating...' : 'Create Project'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


export default CreateProjectDialog;
